import { InfoCard, Progress, Table, TableColumn, WarningPanel } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useKubernetesObjects } from '@backstage/plugin-kubernetes-react';

const ENVIRONMENTS = ['dev', 'qa', 'prod'];

type Row = { env: string; version: string; replicas: string; updated: string };

// El Deployment no lleva la etiqueta app=<servicio> del selector; el ReplicaSet sí (la hereda del pod).
type ReplicaSet = {
  metadata?: { creationTimestamp?: string | Date };
  spec?: { replicas?: number; template?: { spec?: { containers?: { name?: string; image?: string }[] } } };
  status?: { readyReplicas?: number };
};

const createdAt = (rs: ReplicaSet) => new Date(rs.metadata?.creationTimestamp ?? 0).getTime();

const columns: TableColumn<Row>[] = [
  { title: 'Ambiente', field: 'env' },
  { title: 'Versión desplegada', field: 'version' },
  { title: 'Réplicas listas', field: 'replicas' },
  { title: 'Último cambio', field: 'updated' },
];

/** Versión (tag de la imagen) que corre en cada clúster, leída en vivo con el plugin de Kubernetes. */
export const DeployedVersionsCard = () => {
  const { entity } = useEntity();
  const { kubernetesObjects, loading, error } = useKubernetesObjects(entity, 60000);
  if (loading && !kubernetesObjects) return <InfoCard title="Versión por ambiente"><Progress /></InfoCard>;
  if (error) return <WarningPanel title="No se pudo leer la versión desplegada" message={error} />;

  const rows: Row[] = ENVIRONMENTS.map(env => {
    const cluster = kubernetesObjects?.items.find(item => item.cluster.name === env);
    const replicaSets = (cluster?.resources.find(r => r.type === 'replicasets')?.resources ?? []) as ReplicaSet[];
    // El activo es el más reciente con réplicas; si está apagado (p. ej. dev hibernado), el más reciente.
    const sorted = [...replicaSets].sort((a, b) => createdAt(b) - createdAt(a));
    const current = sorted.find(rs => (rs.spec?.replicas ?? 0) > 0) ?? sorted[0];
    if (!current) return { env, version: 'No desplegado', replicas: '-', updated: '-' };
    const containers = (current.spec?.template?.spec?.containers ?? []).filter(c => c.name !== 'istio-proxy');
    const image = (containers.find(c => c.name === entity.metadata.name) ?? containers[0])?.image ?? '';
    return {
      env,
      version: image.includes(':') ? image.split(':').pop()! : image || '-',
      replicas: `${current.status?.readyReplicas ?? 0}/${current.spec?.replicas ?? 0}`,
      updated: new Date(createdAt(current)).toLocaleString('es-EC'),
    };
  });

  return (
    <Table<Row>
      title="Versión por ambiente"
      options={{ paging: false, search: false, padding: 'dense' }}
      columns={columns}
      data={rows}
    />
  );
};
